import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, TextField, Button, Grid, MenuItem, Typography, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { EmployeeDetailsForm } from './EmployeeDetailsModal';


const paymentMethods = [
  { value: 'Bank', label: 'Bank' },
  { value: 'Cash', label: 'Cash' }
];

const superFundTypes = [
  { value: 'Regulated super fund', label: 'Regulated super fund' },
  { value: 'Self managed super fund', label: 'Self managed super fund(SMSF)' }
];

const fontSizes = {
  title: { xs: '1rem', sm: '1.1rem' },
  label: { xs: '0.9rem', sm: '1rem' },
  helper: { xs: '0.75rem', sm: '0.8rem' }
};

const BankAndSuperTab = () => {
  const {
    control,
    formState: { errors },
    watch
  } = useFormContext<EmployeeDetailsForm>();
  const paymentMethod = watch('bankAndSuper.paymentMethod');
  const superFundType = watch('bankAndSuper.superFundType');

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, fontSize: fontSizes.title }}>
        Payment details{' '}
        <IconButton size="small">
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="bankAndSuper.paymentMethod"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Payment method *"
                size="small"
                error={!!errors.bankAndSuper?.paymentMethod}
                helperText={errors.bankAndSuper?.paymentMethod?.message || ''}
                fullWidth
                margin="dense"
              >
                {paymentMethods.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        {paymentMethod === 'Bank' && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.accountName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Account name *"
                    size="small"
                    error={!!errors.bankAndSuper?.accountName}
                    helperText={errors.bankAndSuper?.accountName?.message || ''}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.bank"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Bank *"
                    size="small"
                    error={!!errors.bankAndSuper?.bank}
                    helperText={errors.bankAndSuper?.bank?.message || ''}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.bsb"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="BSB *"
                    size="small"
                    error={!!errors.bankAndSuper?.bsb}
                    helperText={errors.bankAndSuper?.bsb?.message || '6 digits'}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.accountNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Account number *"
                    size="small"
                    error={!!errors.bankAndSuper?.accountNumber}
                    helperText={errors.bankAndSuper?.accountNumber?.message || '6-10 digits'}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{ mt: 3, mb: 2, fontSize: fontSizes.title }}
      >
        Superannuation{' '}
        <Button size="small" sx={{ textTransform: 'none', ml: 1, fontSize: fontSizes.label }}>
          Clear
        </Button>
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="bankAndSuper.superFundType"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Super fund type *"
                size="small"
                error={!!errors.bankAndSuper?.superFundType}
                helperText={errors.bankAndSuper?.superFundType?.message || ''}
                fullWidth
                margin="dense"
              >
                {superFundTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name="bankAndSuper.superContributionRate"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Super contribution rate *"
                size="small"
                error={!!errors.bankAndSuper?.superContributionRate}
                helperText={errors.bankAndSuper?.superContributionRate?.message || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name="bankAndSuper.superCompany"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Type super company, USI or ABN *"
                size="small"
                error={!!errors.bankAndSuper?.superCompany}
                helperText={errors.bankAndSuper?.superCompany?.message || ''}
                fullWidth
                margin="dense"
              />
            )}
          />
        </Grid>
        {superFundType === 'Self managed super fund' && (
          <>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.smsfsName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMSF name *"
                    size="small"
                    error={!!errors.bankAndSuper?.smsfsName}
                    helperText={errors.bankAndSuper?.smsfsName?.message || ''}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.smsfsAbn"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMSF ABN *"
                    size="small"
                    error={!!errors.bankAndSuper?.smsfsAbn}
                    helperText={errors.bankAndSuper?.smsfsAbn?.message || '11 digits'}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.electronicServiceAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Electronic service address *"
                    size="small"
                    error={!!errors.bankAndSuper?.electronicServiceAddress}
                    helperText={
                      errors.bankAndSuper?.electronicServiceAddress?.message ||
                      'e.g., email address'
                    }
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.employerNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Employer number"
                    size="small"
                    error={!!errors.bankAndSuper?.employerNumber}
                    helperText={errors.bankAndSuper?.employerNumber?.message || ''}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.smsfsBankAccountName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMSF bank account name *"
                    size="small"
                    error={!!errors.bankAndSuper?.smsfsBankAccountName}
                    helperText={errors.bankAndSuper?.smsfsBankAccountName?.message || ''}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.smsfsBsb"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMSF BSB *"
                    size="small"
                    error={!!errors.bankAndSuper?.smsfsBsb}
                    helperText={errors.bankAndSuper?.smsfsBsb?.message || '6 digits'}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="bankAndSuper.smsfsAccountNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SMSF account number *"
                    size="small"
                    error={!!errors.bankAndSuper?.smsfsAccountNumber}
                    helperText={errors.bankAndSuper?.smsfsAccountNumber?.message || '6-10 digits'}
                    fullWidth
                    margin="dense"
                  />
                )}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default BankAndSuperTab;
